import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Dog, 
  Bell, 
  Moon, 
  Sun, 
  User, 
  SignOut, 
  GearSix,
  House,
  List,
  PawPrint,
  Crown,
  IdentificationBadge,
  Shield
} from '@phosphor-icons/react';

export function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const { unreadCount } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const isLanding = location.pathname === '/';
  const isProvider = user?.role === 'prestador';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className={`sticky top-0 z-50 w-full ${isLanding ? 'glass-header' : 'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b'}`}>
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <PawPrint weight="fill" className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="font-bold text-xl font-outfit hidden sm:block">PasseioPerfeito</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          {isAuthenticated ? (
            <>
              <Link 
                to={isProvider ? '/provider/dashboard' : '/client/home'}
                className="text-sm font-medium hover:text-primary transition-colors"
                data-testid="nav-home"
              >
                Início
              </Link>
              {!isProvider && (
                <Link 
                  to="/client/providers"
                  className="text-sm font-medium hover:text-primary transition-colors"
                  data-testid="nav-providers"
                >
                  Prestadores
                </Link>
              )}
              <Link 
                to={isProvider ? '/provider/requests' : '/client/requests'}
                className="text-sm font-medium hover:text-primary transition-colors"
                data-testid="nav-requests"
              >
                Pedidos
              </Link>
              {isProvider && (
                <Link 
                  to="/provider/subscription"
                  className="text-sm font-medium hover:text-primary transition-colors"
                  data-testid="nav-subscription"
                >
                  Subscrição
                </Link>
              )}
            </>
          ) : (
            <>
              <a href="#services" className="text-sm font-medium hover:text-primary transition-colors">
                Serviços
              </a>
              <a href="#how-it-works" className="text-sm font-medium hover:text-primary transition-colors">
                Como Funciona
              </a>
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </Button>

          {isAuthenticated ? (
            <>
              <Link to="/notifications" className="relative" data-testid="notifications-link">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="notification-dot" />
                  )}
                </Button>
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(isProvider ? '/provider/profile' : '/client/profile')} data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Perfil
                  </DropdownMenuItem>
                  {isProvider && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/provider/subscription')} data-testid="menu-subscription">
                        <Crown className="mr-2 h-4 w-4" />
                        Subscrição
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/provider/verification')} data-testid="menu-verification">
                        <IdentificationBadge className="mr-2 h-4 w-4" />
                        Verificação
                      </DropdownMenuItem>
                    </>
                  )}
                  {!isProvider && (
                    <DropdownMenuItem onClick={() => navigate('/client/pets')} data-testid="menu-pets">
                      <Dog className="mr-2 h-4 w-4" />
                      Meus Animais
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Shield className="mr-2 h-4 w-4" />
                      Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/account/settings')} data-testid="menu-settings">
                    <GearSix className="mr-2 h-4 w-4" />
                    Definições
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <SignOut className="mr-2 h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/login">
              <Button className="rounded-full" data-testid="login-btn">
                Entrar
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export function MobileNav() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  
  if (!isAuthenticated) return null;

  const isProvider = user?.role === 'prestador';

  const navItems = isProvider ? [
    { to: '/provider/dashboard', icon: House, label: 'Início' },
    { to: '/provider/requests', icon: List, label: 'Pedidos' },
    { to: '/provider/profile', icon: User, label: 'Perfil' },
  ] : [
    { to: '/client/home', icon: House, label: 'Início' },
    { to: '/client/providers', icon: Dog, label: 'Prestadores' },
    { to: '/client/requests', icon: List, label: 'Pedidos' },
    { to: '/client/pets', icon: PawPrint, label: 'Animais' },
  ];

  return (
    <nav className="mobile-nav md:hidden">
      <div className="flex items-center justify-around">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex flex-col items-center gap-1 py-1 px-3 rounded-lg transition-colors ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <Icon weight={isActive ? 'fill' : 'regular'} className="w-6 h-6" />
              <span className="text-xs">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
